import { useState, useEffect } from 'react';
import { reportApi } from '../../api/report.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Skeleton, EmptyState } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { toGregorianDateParams, startOfDay } from '../../utils/jalali';
import JalaliDateRangePicker from '../../components/calendar/JalaliDateRangePicker';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { DownloadSimple, MapPin } from '@phosphor-icons/react';

export default function DistanceReportPage() {
  const { addToast } = useUI();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const today = startOfDay(new Date());
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [type, setType] = useState('employee');

  const buildParams = (output) => {
    // getDistanceReport compares these against mission_date, a real
    // Gregorian MySQL DATETIME (see queryAddMission's NOW()) - the calendar
    // UI is Jalali, but the value sent here must stay Gregorian.
    const s = toGregorianDateParams(startDate);
    const e = toGregorianDateParams(endDate);
    return {
      type,
      output,
      'start_date[year]': s.year, 'start_date[month]': s.month, 'start_date[day]': s.day,
      'end_date[year]': e.year, 'end_date[month]': e.month, 'end_date[day]': e.day,
    };
  };

  const load = (showSpinner = true) => {
    if (!startDate || !endDate) return undefined;
    if (showSpinner) setLoading(true);
    // Backend (getDistanceReport) needs Jalali start_date[year]/[month]/[day]
    // + end_date[...] + type.
    return reportApi.getDistanceReport(buildParams('json'))
      .then(r => setData(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [startDate, endDate, type]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(() => load(false), { paused: exporting });

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await reportApi.getDistanceReport(buildParams('excel'));
      const fileName = res.data?.file_name;
      if (fileName) window.open(BASE_MEDIA_URL + fileName, '_blank');
      else addToast('خروجی اکسل ایجاد نشد', 'error');
    } catch { addToast('خطا در ایجاد خروجی اکسل', 'error'); }
    setExporting(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="گزارش مسافت"
        action={<Btn variant="secondary" onClick={exportExcel} loading={exporting} disabled={data.length === 0}><DownloadSimple size={14} weight="regular" /> خروجی اکسل</Btn>}
      />
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <JalaliDateRangePicker startDate={startDate} endDate={endDate} onChange={(s, e) => { setStartDate(s); setEndDate(e); }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[['employee', 'کارمند'], ['company', 'شرکت']].map(([id, label]) => (
            <button key={id} onClick={() => setType(id)} style={{ padding: '7px 14px', borderRadius: 20, border: `1.5px solid ${type === id ? '#29AAE1' : '#e2e8f0'}`, background: type === id ? '#EDF3FC' : '#fff', color: type === id ? '#233A7A' : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
          ))}
        </div>
      </div>
      {loading ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1, 2, 3].map(k => <div key={k} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0' }}><Skeleton h={16} /></div>)}</div>
        : data.length === 0 ? <EmptyState icon={<MapPin size={44} weight="regular" />} title="داده‌ای برای این بازه زمانی یافت نشد" />
        : <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(35,58,122,0.1)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '12px 20px', background: '#f8faff', borderBottom: '1px solid #e2e8f0', fontSize: 12, fontWeight: 700, color: '#64748b' }}>
              <span>کارمند</span><span>مسافت (کیلومتر)</span><span>وضعیت</span>
            </div>
            {data.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '13px 20px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#334155', background: i % 2 === 0 ? '#fff' : '#fafbff' }}>
                <span>{row.name || row.username}</span>
                <span style={{ fontWeight: 700, color: '#233A7A' }}>{toPersian(row.distance || 0)} km</span>
                <span>{row.status || '—'}</span>
              </div>
            ))}
          </div>
      }
    </div>
  );
}
